// src/pages/Expedientes/ExpedienteDetalle.js
import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useParams } from "react-router-dom";
import {
    Box, Button, Typography, CircularProgress, Grid, Tabs, Tab, Chip,
    useMediaQuery, Paper, Tooltip, Snackbar, IconButton, Dialog, DialogTitle,
    DialogContent, DialogActions, Collapse, TextField, MenuItem,
    Radio, RadioGroup, FormControlLabel
} from "@mui/material";
import { Alert as MuiLabAlert } from '@mui/material';
import ExpedienteDetalleBreadcrumb from "../../components/ExpedienteDetalleBreadcrumb";
import HistorialCambios from "./HistorialCambios";
import MovimientosTab from "./MovimientosTab";
import PartesTab from "./PartesTab";
import InmueblesTab from './InmueblesTab';
import ConcursosTab from './ConcursosTab';
import MediacionesTab from './MediacionesTab';
import ExpedienteForm from "./ExpedienteForm";
import MuiAlert from '@mui/material/Alert';
import RadicacionTab from "./RadicacionTab";
import { capitalizar } from "../../utils/capitalizar";
import CertificadosTab from "./CertificadosTab";
import VinculadosTab from "./VinculadosTab";
import NotasTab from "./NotasTab";
import EventosTab from "./EventosTab";
import SolicitudesPagoTab from "./SolicitudesPagoTab";
import PasesTab from "./PasesTab";
import ResolucionesTab from "./ResolucionesTab";
import EnviarPaseDialog from "./EnviarPaseDialog";
import {
    expedienteDetalleJWT, getCombosJWT,
    expedienteAreasConjuntasJWT, agregarAreaConjuntaJWT, eliminarAreaConjuntaJWT,
    expedienteRiesgoRelevanciaJWT, guardarRiesgoRelevanciaJWT, editarExpedienteJWT,
    expedienteEmbargosJWT, hasSolicitudesPagoJWT, ubicacionExpedienteJWT,
} from "../../api/endpoints";
import { usePermisos } from "../../utils/usePermisos";
import usePermisosModulo from "../../hooks/usePermisosModulo";
import { normalizeResponse } from "../../utils/normalizeKeys";
import AddIcon from "@mui/icons-material/Add";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import EditIcon from "@mui/icons-material/Edit";
import InfoIcon from "@mui/icons-material/Info";
import HomeIcon from "@mui/icons-material/Home";
import DescriptionIcon from "@mui/icons-material/Description";
import GavelIcon from "@mui/icons-material/Gavel";
import PeopleIcon from "@mui/icons-material/People";
import TimelineIcon from "@mui/icons-material/Timeline";
import LinkIcon from "@mui/icons-material/Link";
import HistoryIcon from "@mui/icons-material/History";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import PaymentIcon from "@mui/icons-material/Payment";
import FlagIcon from "@mui/icons-material/Flag";
import CancelIcon from "@mui/icons-material/Cancel";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import HandshakeIcon from "@mui/icons-material/Handshake";
import StickyNote2Icon from "@mui/icons-material/StickyNote2";
import EventIcon from "@mui/icons-material/Event";
import SendIcon from "@mui/icons-material/Send";
import CaratulaAdminDialog from "../../components/Expedientes/CaratulaAdminDialog";

function AlertSnackbar(props) { return <MuiAlert elevation={6} variant="filled" {...props} />; }

export default function ExpedienteDetalle({ usuario }) {
    const { tipo, id } = useParams();

    const [openEdit, setOpenEdit] = useState(false);
    const [caratulaOpen, setCaratulaOpen] = useState(false);
    const [tab, setTab] = useState(0);

    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    const [historial, setHistorial] = useState([]);
    const [loadingHistorial] = useState(false);

    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

    // Permisos granulares por módulo (hook centralizado)
    const { permisosExpediente } = usePermisos();

    const [areasConjuntas, setAreasConjuntas] = useState([]);
    const [dialogAreaConjunta, setDialogAreaConjunta] = useState(false);
    const [modulosDisponibles, setModulosDisponibles] = useState([]);
    const [moduloSeleccionado, setModuloSeleccionado] = useState(null);

    // Permisos de riesgo judicial por módulo
    const { permDe: permDeModulo } = usePermisosModulo(data?.modulo || data?.area);
    const permRiesgo = permDeModulo("PESTAÑA RIESGO JUDICIAL");

    // ⭐ AGREGAR estos nuevos estados para Riesgo y Relevancia:
    const [riesgoRelevancia, setRiesgoRelevancia] = useState(null);
    const [esFiscal, setEsFiscal] = useState(false);

    // Solicitudes de pago (para mostrar/ocultar tab en admin)
    const [tieneSolicitudesPago, setTieneSolicitudesPago] = useState(false);

    // Ubicación + Pases
    const [ubicacion, setUbicacion] = useState(null);
    const [enviarPaseOpen, setEnviarPaseOpen] = useState(false);
    const [pasesRefreshKey, setPasesRefreshKey] = useState(0);

    // Determinar si el usuario actual puede enviar el expediente
    const puedeEnviarPase = !ubicacion // sin ubicación → puede iniciar
        || (ubicacion.estado === 'aceptado' && ubicacion.usuario === usuario?.usuario);
    const motivoNoPuedeEnviar = ubicacion
        ? ubicacion.estado === 'en_transito'
            ? 'Pase pendiente de aceptación'
            : ubicacion.usuario !== usuario?.usuario
                ? `Lo tiene ${capitalizar(ubicacion.usuarioNombre)}`
                : ''
        : '';
    const [embargos, setEmbargos] = useState([]);
    const [embargoDetalle, setEmbargoDetalle] = useState(null);
    const [dialogRiesgoRelevancia, setDialogRiesgoRelevancia] = useState(false);
    const [riesgoRelevanciaForm, setRiesgoRelevanciaForm] = useState({
        relevancia_economica: 4,
        relevancia_institucional: 4,
        relevancia_social: 4,
        relevancia_juridica: 4,
        riesgo_judicial: 4,
        ejercicio_presupuestario: 3,
        acceso_visibilidad: 4,
        obs_re: "",
        obs_ri: "",
        obs_rs: "",
        obs_rj: "",
        obs_rg: "",
        obs_ep: "",
        obs_av: ""
    });

    const [infoExpanded, setInfoExpanded] = useState(false);

    const isMobile = useMediaQuery("(max-width:600px)");

    // ------- data (endpoint individual: detalle + historial) -------
    const reloadData = useCallback(async () => {
        if (!id) return;
        setLoading(true);
        try {
            const response = await expedienteDetalleJWT(id);

            if (response && response.ok && response.expediente) {
                const normalized = normalizeResponse(response.expediente);
                // Flatten: copiar fecha_ultima_modificacion desde auditoria al nivel raíz
                if (normalized.auditoria?.fecha_ultima_modificacion && !normalized.fecha_ultima_modificacion) {
                    normalized.fecha_ultima_modificacion = normalized.auditoria.fecha_ultima_modificacion;
                }
                setData(normalized);
                // Historial viene dentro de auditoria
                const hist = normalized?.auditoria?.historial || [];
                setHistorial(hist);
            } else {
                setSnackbar({
                    open: true,
                    message: response?.mensaje || "Error al cargar el expediente",
                    severity: "error"
                });
            }
        } catch (err) {
            console.error("Error al cargar expediente:", err);
            setSnackbar({ open: true, message: "Error al cargar el expediente", severity: "error" });
        } finally {
            setLoading(false);
        }
    }, [id]);

    // Refresh silencioso solo del historial (sin parpadeo)
    const refreshHistorial = useCallback(async () => {
        if (!id) return;
        try {
            const response = await expedienteDetalleJWT(id);
            if (response?.ok && response.expediente) {
                const normalized = normalizeResponse(response.expediente);
                setHistorial(normalized?.auditoria?.historial || []);
            }
        } catch { /* silencioso */ }
    }, [id]);

    // --- guardar edición desde el modal ---
    const handleSaveEdit = async (payload) => {
        try {
            const resp = await editarExpedienteJWT(id, payload);
            if (resp?.ok) {
                setOpenEdit(false);
                setSnackbar({ open: true, message: "Expediente actualizado correctamente", severity: "success" });
                reloadData();
            } else {
                setSnackbar({ open: true, message: resp?.mensaje || "Error al guardar expediente", severity: "error" });
            }
        } catch (err) {
            console.error("Error al editar expediente:", err);
            setSnackbar({ open: true, message: err?.message || "Error al guardar expediente", severity: "error" });
        }
    };

    // ⭐ ID del módulo actual del expediente
    const idModuloActual = useMemo(() => {
        return data?.id_modulo ? Number(data.id_modulo) : 0;
    }, [data]);

    // Permisos calculados para este expediente (módulo origen + áreas conjuntas)
    const permsExp = useMemo(() => {
        const idsConjuntas = areasConjuntas.map(a => Number(a.id_modulo));
        return permisosExpediente(idModuloActual, idsConjuntas);
    }, [permisosExpediente, idModuloActual, areasConjuntas]);

    // Areas conjuntas
    const loadAreasConjuntas = useCallback(async () => {
        try {
            const resp = await expedienteAreasConjuntasJWT(id);
            if (resp?.ok) {
                setAreasConjuntas(resp.areas || []);
            }
        } catch (err) {
            console.error("Error cargando áreas conjuntas:", err);
        }
    }, [id]);

    // Riesgo y relevancia
    const loadRiesgoRelevancia = useCallback(async () => {
        try {
            const resp = await expedienteRiesgoRelevanciaJWT(id);
            if (resp?.ok) {
                setRiesgoRelevancia(resp.data || null);
                setEsFiscal(!!resp.es_fiscal);
            }
        } catch (err) {
            console.error("Error cargando riesgo/relevancia:", err);
        }
    }, [id]);

    // Embargos (para Datos Principales)
    const loadEmbargos = useCallback(async () => {
        try {
            const resp = await expedienteEmbargosJWT(id);
            if (resp?.ok) {
                setEmbargos(resp.embargos || []);
            }
        } catch (err) {
            console.error("Error cargando embargos:", err);
        }
    }, [id]);

    // ⭐ Guardar Riesgo y Relevancia
    const handleGuardarRiesgoRelevancia = async () => {
        try {
            const resp = await guardarRiesgoRelevanciaJWT(id, riesgoRelevanciaForm);
            if (resp?.ok) {
                setSnackbar({ open: true, message: "Riesgo y relevancia guardado", severity: "success" });
                await loadRiesgoRelevancia();
            } else {
                setSnackbar({ open: true, message: resp?.mensaje || "Error al guardar", severity: "error" });
            }
        } catch (err) {
            console.error("Error guardando riesgo/relevancia:", err);
            setSnackbar({ open: true, message: "Error al guardar riesgo y relevancia", severity: "error" });
        }
        setDialogRiesgoRelevancia(false);
    };

    // ⭐ Abrir dialog con datos actuales
    const handleAbrirRiesgoRelevancia = () => {
        if (riesgoRelevancia) {
            setRiesgoRelevanciaForm({
                relevancia_economica: riesgoRelevancia.relevancia_economica || 4,
                relevancia_institucional: riesgoRelevancia.relevancia_institucional || 4,
                relevancia_social: riesgoRelevancia.relevancia_social || 4,
                relevancia_juridica: riesgoRelevancia.relevancia_juridica || 4,
                riesgo_judicial: riesgoRelevancia.riesgo_judicial || 4,
                ejercicio_presupuestario: riesgoRelevancia.ejercicio_presupuestario || 3,
                acceso_visibilidad: riesgoRelevancia.acceso_visibilidad || 4,
                obs_re: riesgoRelevancia.obs_re || "",
                obs_ri: riesgoRelevancia.obs_ri || "",
                obs_rs: riesgoRelevancia.obs_rs || "",
                obs_rj: riesgoRelevancia.obs_rj || "",
                obs_rg: riesgoRelevancia.obs_rg || "",
                obs_ep: riesgoRelevancia.obs_ep || "",
                obs_av: riesgoRelevancia.obs_av || ""
            });
        }
        setDialogRiesgoRelevancia(true);
    };

    // Cargar módulos disponibles
    const loadModulosDisponibles = useCallback(async () => {
        try {
            const response = await getCombosJWT('modulos');
            if (response?.ok) {
                setModulosDisponibles(response.items || response.datos || response.modulos || []);
            }
        } catch (err) {
            console.error("Error cargando módulos:", err);
        }
    }, []);

    // Agregar área conjunta
    const handleAgregarAreaConjunta = () => {
        loadModulosDisponibles();
        setDialogAreaConjunta(true);
    };

    const handleGuardarAreaConjunta = async () => {
        if (!moduloSeleccionado) return;
        try {
            const resp = await agregarAreaConjuntaJWT(id, moduloSeleccionado);
            if (resp?.ok) {
                setSnackbar({ open: true, message: "Área conjunta agregada", severity: "success" });
                await loadAreasConjuntas();
            } else {
                setSnackbar({ open: true, message: resp?.mensaje || "Error al agregar", severity: "error" });
            }
        } catch (err) {
            console.error("Error agregando área conjunta:", err);
            setSnackbar({ open: true, message: "Error al agregar área conjunta", severity: "error" });
        }
        setDialogAreaConjunta(false);
        setModuloSeleccionado(null);
    };

    const handleEliminarAreaConjunta = async (idArea) => {
        try {
            const resp = await eliminarAreaConjuntaJWT(id, idArea);
            if (resp?.ok) {
                setSnackbar({ open: true, message: "Área conjunta eliminada", severity: "success" });
                await loadAreasConjuntas();
            } else {
                setSnackbar({ open: true, message: resp?.mensaje || "Error al eliminar", severity: "error" });
            }
        } catch (err) {
            console.error("Error eliminando área conjunta:", err);
            setSnackbar({ open: true, message: "Error al eliminar área conjunta", severity: "error" });
        }
    };

    // ⭐ Tabs dinámicas según área/módulo
    const tabsArr = useMemo(() => {
        if (tipo === "judicial") {
            const idModulo = idModuloActual;
            // ⭐ Obtener todos los módulos (principal + conjuntos)
            const todosLosModulos = [idModulo, ...areasConjuntas.map(a => Number(a.id_modulo))];

            let tabs = ["Radicación"]; // Siempre empieza con Radicación

            // ⭐ Certificados: si ALGUNO de los módulos es 1 o 2
            if (todosLosModulos.some(m => m === 1 || m === 2)) {
                tabs.push("Certificados");
            }

            // Inmuebles - si ALGUNO de los módulos es 3
            if (todosLosModulos.some(m => m === 3)) {
                tabs.push("Inmuebles");
            }

            // ⭐ Concursos y Quiebras - si ALGUNO de los módulos es 4
            if (todosLosModulos.some(m => m === 4)) {
                tabs.push("Concursos y Quiebras");
            }

            tabs.push("Resoluciones");

            // Todos tienen Partes y Movimientos
            tabs.push("Partes", "Movimientos");

            // Todos tienen Vinculados
            tabs.push("Vinculados");

            tabs.push("Pases");

            // Todos tienen Notas e Historial
            tabs.push("Eventos", "Notas", "Historial");

            return tabs;
        } else {
            // Administrativos
            const idModulo = idModuloActual;
            const todosLosModulos = [idModulo, ...areasConjuntas.map(a => Number(a.id_modulo))];

            let tabs = ["Movimientos", "Vinculados"];

            // Inmuebles - si ALGUNO de los módulos es 3
            if (todosLosModulos.some(m => m === 3)) {
                tabs.push("Inmuebles");
            }

            // ⭐ Mediaciones - si ALGUNO de los módulos es 5 o 15 (área origen o conjunta)
            if (todosLosModulos.some(m => m === 5 || m === 15)) {
                tabs.push("Mediaciones");
            }

            tabs.push("Resoluciones", "Pases");
            if (tieneSolicitudesPago) tabs.push("Solicitudes de Pago");
            tabs.push("Eventos", "Notas", "Historial");

            return tabs;
        }
    }, [tipo, idModuloActual, areasConjuntas, tieneSolicitudesPago]);

    // ⭐ Funciones helper para Riesgo y Relevancia
    const getTextoRelevancia = (valor, tipo) => {
        if (tipo === "economica") {
            if (valor === 1) return "Alta";
            if (valor === 2) return "Media";
            if (valor === 3) return "Baja";
            return "No Informada";
        } else {
            if (valor === 1) return "Alta";
            return "No Registra";
        }
    };

    const getTextoRiesgo = (valor) => {
        if (valor === 1) return "Alta";
        if (valor === 2) return "Media";
        if (valor === 3) return "Baja";
        return "No Informada";
    };

    const getTextoEjercicio = (valor) => {
        if (valor === 1) return "En Curso";
        if (valor === 2) return "En el Próximo";
        return "No Informada";
    };

    const getTextoAcceso = (valor) => {
        if (valor === 1) return "Solo Fiscal";
        return "Normal";
    };

    const getColorChip = (valor, tipo) => {
        if (tipo === "economica") {
            if (valor === 1) return { bg: '#ffebee', color: '#c62828', border: '#ef5350' }; // Alta = Rojo
            if (valor === 2) return { bg: '#fff3e0', color: '#e65100', border: '#ff9800' }; // Media = Naranja
            if (valor === 3) return { bg: '#e8f5e9', color: '#2e7d32', border: '#66bb6a' }; // Baja = Verde
            return { bg: '#f5f5f5', color: '#757575', border: '#bdbdbd' }; // No Informada = Gris
        } else {
            if (valor === 1) return { bg: '#ffebee', color: '#c62828', border: '#ef5350' }; // Alta = Rojo
            return { bg: '#f5f5f5', color: '#757575', border: '#bdbdbd' }; // No Registra = Gris
        }
    };

    // ⭐ Verificar si hay alguna alerta alta
    const tieneAlertasAltas = useMemo(() => {
        if (!riesgoRelevancia || tipo !== "judicial") return false;
        return (
            riesgoRelevancia.relevancia_economica === 1 ||
            riesgoRelevancia.relevancia_institucional === 1 ||
            riesgoRelevancia.relevancia_social === 1 ||
            riesgoRelevancia.relevancia_juridica === 1 ||
            riesgoRelevancia.riesgo_judicial === 1
        );
    }, [riesgoRelevancia, tipo]);

    // ------- effects -------
    useEffect(() => { setTab(0); }, [tipo]); // al cambiar tipo, volver a la primera pestaña

    useEffect(() => {
        reloadData();
    }, [reloadData]);

    useEffect(() => {
        if (id) {
            loadAreasConjuntas();
            loadRiesgoRelevancia();
            loadEmbargos();
        }
    }, [id, loadAreasConjuntas, loadRiesgoRelevancia, loadEmbargos]);

    // Verificación rápida de solicitudes de pago (solo admin, query COUNT liviana)
    useEffect(() => {
        if (id && tipo !== 'judicial') {
            hasSolicitudesPagoJWT(id).then(resp => {
                setTieneSolicitudesPago(resp?.ok && resp.tiene === true);
            }).catch(() => setTieneSolicitudesPago(false));
        }
    }, [id, tipo]);

    // Cargar ubicación actual del expediente
    const cargarUbicacion = useCallback(() => {
        if (!id) return;
        ubicacionExpedienteJWT(id).then(resp => {
            if (resp?.ok) setUbicacion(resp.ubicacion || null);
        }).catch(() => {});
    }, [id]);

    useEffect(() => { cargarUbicacion(); }, [cargarUbicacion]);

    // Polling de ubicación cada 30s (para que el receptor vea cambios)
    useEffect(() => {
        const interval = setInterval(cargarUbicacion, 30000);
        return () => clearInterval(interval);
    }, [cargarUbicacion]);

    // ------- componente compacto para campos de datos -------
    const themeColor = tipo === "judicial" ? '#1976d2' : '#d32f2f';

    const DataField = ({ label, value, isDate }) => (
        <Box sx={{ py: 0.5, px: 1 }}>
            <Typography sx={{ color: '#999', fontWeight: 600, textTransform: 'uppercase', fontSize: '0.6rem', letterSpacing: 0.5, lineHeight: 1.2 }}>
                {label}
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 500, color: isDate ? '#2e7d32' : '#333', mt: 0.25 }}>
                {value || "—"}
            </Typography>
        </Box>
    );

    // ------- aviso campos clave (actualizado a los nuevos encabezados) -------
    const camposClave = tipo === "judicial"
        ? [{ campo: "caratula", nombre: "Carátula" }, { campo: "estado_proceso", nombre: "Estado" }]
        : [{ campo: "nro_expediente", nombre: "Nº Expediente" }, { campo: "extracto", nombre: "Extracto" }, { campo: "iniciador", nombre: "Iniciador" }];
    const faltanCampos = camposClave.filter(c => !data?.[c.campo] || data?.[c.campo] === "-");
        
    if (loading) return <Box p={4}><CircularProgress /> Cargando expediente...</Box>;
    if (!data || data.ok === false) return <Box p={4}><Typography>No se encontró el expediente.</Typography></Box>;

    // ------- overrides del formulario (solo Administrativo) -------
    const uiAdminOverrides = tipo !== "judicial" ? {
        order: [
            ["nro_expediente", { required: true, label: "Nº Expediente" }],
            ["extracto", { required: true, label: "Extracto", fullWidth: true }],
            ["iniciador", { required: true, label: "Iniciador" }],
            ["tipo_tramite", { required: false, label: "Tipo de Trámite" }],
            ["situacion", { required: false, label: "Situación" }],
            ["objeto_reclamo", { required: false, label: "Objeto de Reclamo" }],
        ],
        hide: ["area_origen", "modulo"]
    } : undefined;

    return (
        <Box p={isMobile ? 1 : 2}>
            <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
                <ExpedienteDetalleBreadcrumb tipo={tipo} data={data} />

                {/* Ubicación actual + Enviar pase */}
                <Box
                    display="flex"
                    alignItems="center"
                    gap={0.5}
                    sx={{
                        backgroundColor: 'rgba(255,255,255,0.15)',
                        backdropFilter: 'blur(4px)',
                        borderRadius: 1,
                        pl: 1.5,
                        pr: 0.5,
                        py: 0.25,
                    }}
                >
                    <LocationOnIcon sx={{ fontSize: 16, color: '#fff', opacity: 0.8 }} />
                    <Typography variant="caption" sx={{ color: '#fff', opacity: 0.9, fontWeight: 500, whiteSpace: 'nowrap', maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {ubicacion
                            ? ubicacion.estado === 'en_transito'
                                ? `En tránsito → ${capitalizar(ubicacion.usuarioNombre)}`
                                : capitalizar(ubicacion.usuarioNombre)
                            : 'Sin ubicación'
                        }
                    </Typography>
                    <Tooltip title={puedeEnviarPase ? "Enviar expediente" : motivoNoPuedeEnviar} arrow>
                        <span>
                            <IconButton
                                size="small"
                                onClick={() => setEnviarPaseOpen(true)}
                                disabled={!puedeEnviarPase}
                                sx={{
                                    color: '#fff',
                                    backgroundColor: puedeEnviarPase ? 'rgba(255,255,255,0.2)' : 'rgba(255,255,255,0.08)',
                                    '&:hover': { backgroundColor: 'rgba(255,255,255,0.35)' },
                                    '&.Mui-disabled': { color: 'rgba(255,255,255,0.3)' },
                                    width: 26,
                                    height: 26,
                                    ml: 0.5,
                                }}
                            >
                                <SendIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                        </span>
                    </Tooltip>
                </Box>
            </Box>

            <EnviarPaseDialog
                open={enviarPaseOpen}
                onClose={() => setEnviarPaseOpen(false)}
                idExpediente={id}
                onEnviado={() => { cargarUbicacion(); setPasesRefreshKey(k => k + 1); }}
            />

            <Paper elevation={3} style={{ borderRadius: 4, padding: isMobile ? 12 : 24, marginBottom: 24 }}>
                {faltanCampos.length > 0 && (
                    <Box mb={2}>
                        <MuiLabAlert severity="warning" variant="filled">
                            Atención: faltan datos importantes en el expediente: {faltanCampos.map(c => c.nombre).join(", ")}
                        </MuiLabAlert>
                    </Box>
                )}

                {/* Header Visual Mejorado */}
                <Box
                    sx={{
                        background: tipo === "judicial"
                            ? 'linear-gradient(90deg, #1976d2 0%, #1565c0 100%)'
                            : 'linear-gradient(90deg, #d32f2f 0%, #c62828 100%)',
                        color: 'white',
                        padding: '16px 24px',
                        borderRadius: '4px',
                        marginBottom: '12px',
                        boxShadow: (tipo === "judicial" && tieneAlertasAltas)
                            ? '0 0 0 2px #ff5252, 0 2px 12px rgba(255,82,82,0.3)'
                            : '0 2px 8px rgba(0,0,0,0.15)',
                        ...((tipo === "judicial" && tieneAlertasAltas) ? {
                            animation: 'pulse-border 2s ease-in-out infinite',
                            '@keyframes pulse-border': {
                                '0%, 100%': { boxShadow: '0 0 0 2px #ff5252, 0 2px 12px rgba(255,82,82,0.3)' },
                                '50%': { boxShadow: '0 0 0 4px #ff5252, 0 0 20px rgba(255,82,82,0.5)' },
                            },
                        } : {}),
                    }}
                >
                    <Grid container spacing={2} alignItems="center">
                        <Grid size={{ xs: 12, md: tipo === "judicial" ? 7 : 8 }}>
                            {/* Título */}
                            <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                                <span role="img" aria-label={tipo === "judicial" ? "justicia" : "documento"} style={{ fontSize: 22 }}>
                                    {tipo === "judicial" ? "⚖️" : "📋"}
                                </span>
                                <Typography variant="h6" style={{ fontWeight: 700, lineHeight: 1.2 }}>
                                    {tipo === "judicial"
                                        ? capitalizar(data?.caratula || "-")
                                        : capitalizar(data?.extracto || "-")
                                    }
                                </Typography>
                            </Box>

                            {/* Subtítulo con más info */}
                            <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
                                <Typography variant="body2" style={{ opacity: 0.9 }}>
                                    {tipo === "judicial"
                                        ? `Autos: ${data?.autos_1_instancia || "-"}`
                                        : `Nº Expediente: ${data?.nro_expediente || "-"}`
                                    }
                                </Typography>

                                {/* Indicador de áreas conjuntas */}
                                {areasConjuntas.length > 0 && (
                                    <Chip
                                        size="small"
                                        label={`+${areasConjuntas.length} área${areasConjuntas.length > 1 ? 's' : ''}`}
                                        style={{
                                            backgroundColor: 'rgba(255,255,255,0.15)',
                                            color: 'white',
                                            fontWeight: 600,
                                            height: 24,
                                            fontSize: 11
                                        }}
                                    />
                                )}
                            </Box>
                        </Grid>

                        <Grid size={{ xs: 12, md: tipo === "judicial" ? 5 : 4 }}>
                            <Box display="flex" gap={2} justifyContent="flex-end" alignItems="center" flexWrap="wrap">
                                {/* Chip de estado */}
                                <Chip
                                    label={data?.estado_proceso || data?.estado || "Sin estado"}
                                    style={{
                                        backgroundColor: 'white',
                                        color: tipo === "judicial" ? '#1976d2' : '#d32f2f',
                                        fontWeight: 700,
                                        fontSize: 13,
                                        padding: '4px 12px',
                                        height: 32,
                                        marginRight: 8
                                    }}
                                />

                                {/* Indicador de alerta si hay relevancias altas */}
                                {tipo === "judicial" && tieneAlertasAltas && (
                                    <Tooltip title="Tiene relevancias o riesgo ALTO">
                                        <Box sx={{
                                            display: 'flex', alignItems: 'center', gap: 0.75,
                                            backgroundColor: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(4px)',
                                            border: '1px solid rgba(255,255,255,0.3)',
                                            borderRadius: 1, px: 1.5, py: 0.5, mr: 1,
                                        }}>
                                            <Box sx={{
                                                width: 8, height: 8, borderRadius: '50%', backgroundColor: '#ff5252',
                                                boxShadow: '0 0 6px #ff5252',
                                                animation: 'pulse-dot 2s ease-in-out infinite',
                                                '@keyframes pulse-dot': {
                                                    '0%, 100%': { opacity: 1, transform: 'scale(1)' },
                                                    '50%': { opacity: 0.5, transform: 'scale(1.4)' },
                                                },
                                            }} />
                                            <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '0.65rem', letterSpacing: 1, textTransform: 'uppercase' }}>
                                                Alerta
                                            </Typography>
                                        </Box>
                                    </Tooltip>
                                )}

                                {/* Botón Carátula - solo expedientes administrativos */}
                                {tipo !== "judicial" && (
                                    <Tooltip title="Carátula del expediente (imprimir / PDF)">
                                        <IconButton
                                            size="small"
                                            onClick={() => setCaratulaOpen(true)}
                                            style={{
                                                backgroundColor: 'white',
                                                color: '#d32f2f',
                                                padding: 8,
                                                marginRight: 8
                                            }}
                                        >
                                            <DescriptionIcon />
                                        </IconButton>
                                    </Tooltip>
                                )}

                                {/* ⭐ Botón Info - Expande/colapsa información adicional */}
                                <Tooltip title={infoExpanded ? "Ocultar información" : "Ver información adicional"}>
                                    <IconButton
                                        size="small"
                                        onClick={() => setInfoExpanded(!infoExpanded)}
                                        style={{
                                            backgroundColor: 'white',
                                            color: tipo === "judicial" ? '#1976d2' : '#d32f2f',
                                            padding: 8,
                                            marginRight: 8
                                        }}
                                    >
                                        {infoExpanded ? <ExpandLessIcon /> : <InfoIcon />}
                                    </IconButton>
                                </Tooltip>

                                {/* ⭐ Botón Editar - Solo ícono */}
                                {permsExp.origen.editar && (
                                    <Tooltip title="Editar expediente">
                                        <IconButton
                                            size="small"
                                            onClick={() => setOpenEdit(true)}
                                            style={{
                                                backgroundColor: 'white',
                                                color: tipo === "judicial" ? '#1976d2' : '#d32f2f',
                                                padding: 8
                                            }}
                                        >
                                            <EditIcon />
                                        </IconButton>
                                    </Tooltip>
                                )}
                            </Box>
                        </Grid>
                    </Grid>
                </Box>

                {/* ⭐ Info expandible con Collapse */}
                <Collapse in={infoExpanded} timeout="auto">
                    <Box sx={{ mb: 1.5, border: '1px solid #e0e0e0', borderRadius: 1, overflow: 'hidden' }}>

                        {/* DATOS PRINCIPALES */}
                        <Box sx={{ px: 2, pt: 1.5, pb: 1, backgroundColor: '#fafbfc', borderBottom: '1px solid #eee' }}>
                            <Box display="flex" alignItems="center" gap={1}>
                                <InfoIcon sx={{ fontSize: 16, color: themeColor }} />
                                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#444', letterSpacing: 0.3 }}>
                                    Datos Principales
                                </Typography>
                            </Box>
                        </Box>
                        <Box sx={{ px: 1, py: 1 }}>
                            <Grid container spacing={0}>
                                {tipo === "judicial" ? (
                                    <>
                                        <Grid size={{ xs: 6, sm: 4, md: 3 }}><DataField label="Área Origen" value={capitalizar(data?.modulo)} /></Grid>
                                        <Grid size={{ xs: 6, sm: 4, md: 3 }}><DataField label="Tipo Proceso" value={capitalizar(data?.tipo_proceso)} /></Grid>
                                        <Grid size={{ xs: 6, sm: 4, md: 3 }}><DataField label="Objeto de Reclamo" value={capitalizar(data?.objeto_reclamo)} /></Grid>
                                        <Grid size={{ xs: 6, sm: 4, md: 3 }}><DataField label="Situación" value={capitalizar(data?.estado_proceso)} /></Grid>
                                        <Grid size={{ xs: 6, sm: 4, md: 3 }}><DataField label="Fecha Inicio" value={data?.fecha_inicio?.substring(0, 10)} isDate /></Grid>
                                        <Grid size={{ xs: 6, sm: 4, md: 3 }}><DataField label="Fecha Asignación" value={data?.fecha_asignacion?.substring(0, 10)} isDate /></Grid>
                                        <Grid size={{ xs: 6, sm: 4, md: 3 }}><DataField label="Última Actualización" value={data?.fecha_ultima_modificacion !== "-" ? data?.fecha_ultima_modificacion?.substring(0, 10) : null} isDate /></Grid>
                                    </>
                                ) : (
                                    <>
                                        <Grid size={{ xs: 6, sm: 4, md: 3 }}><DataField label="Área Origen" value={capitalizar(data?.modulo)} /></Grid>
                                        <Grid size={{ xs: 6, sm: 4, md: 3 }}><DataField label="Repartición Origen" value={capitalizar(data?.reparticion_origen)} /></Grid>
                                        <Grid size={{ xs: 6, sm: 4, md: 3 }}><DataField label="Iniciador" value={capitalizar(data?.iniciador)} /></Grid>
                                        <Grid size={{ xs: 6, sm: 4, md: 3 }}><DataField label="Fecha Inicio" value={data?.fecha_inicio?.substring(0, 10)} isDate /></Grid>
                                        <Grid size={{ xs: 6, sm: 4, md: 3 }}><DataField label="Última Actualización" value={data?.fecha_ultima_modificacion !== "-" ? data?.fecha_ultima_modificacion?.substring(0, 10) : null} isDate /></Grid>
                                    </>
                                )}
                            </Grid>
                        </Box>

                        {/* ÁREAS CONJUNTAS */}
                        <Box sx={{ px: 2, pt: 1, pb: 0.75, backgroundColor: '#fafbfc', borderTop: '1px solid #eee', borderBottom: '1px solid #eee' }}>
                            <Box display="flex" alignItems="center" justifyContent="space-between">
                                <Box display="flex" alignItems="center" gap={1}>
                                    <LinkIcon sx={{ fontSize: 16, color: themeColor }} />
                                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#444', letterSpacing: 0.3 }}>
                                        Áreas Conjuntas
                                    </Typography>
                                </Box>
                                {permsExp.origen.editar && (
                                    <Button size="small" startIcon={<AddIcon />} onClick={handleAgregarAreaConjunta}
                                        sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.75rem', color: themeColor, minWidth: 'auto', py: 0.25 }}>
                                        Agregar
                                    </Button>
                                )}
                            </Box>
                        </Box>
                        <Box sx={{ px: 2, py: 1.5 }}>
                            {areasConjuntas.length === 0 ? (
                                <Typography variant="caption" sx={{ color: '#999', fontStyle: 'italic' }}>
                                    No hay áreas conjuntas asignadas
                                </Typography>
                            ) : (
                                <Box display="flex" gap={1} flexWrap="wrap">
                                    {areasConjuntas.map(area => (
                                        <Chip key={area.id} label={capitalizar(area.modulo)} size="small"
                                            onDelete={permsExp.origen.editar ? () => handleEliminarAreaConjunta(area.id) : undefined}
                                            sx={{ border: `1.5px solid ${themeColor}`, color: themeColor, fontWeight: 600, fontSize: '0.75rem', backgroundColor: 'transparent' }}
                                            deleteIcon={permsExp.origen.editar ? <CancelIcon sx={{ fontSize: 16, color: themeColor }} /> : undefined}
                                        />
                                    ))}
                                </Box>
                            )}
                        </Box>

                        {/* EMBARGOS (si hay) */}
                        {embargos.length > 0 && (
                            <>
                                <Box sx={{ px: 2, pt: 1, pb: 0.75, backgroundColor: '#fafbfc', borderTop: '1px solid #eee', borderBottom: '1px solid #eee' }}>
                                    <Box display="flex" alignItems="center" gap={1}>
                                        <AccountBalanceIcon sx={{ fontSize: 16, color: themeColor }} />
                                        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#444', letterSpacing: 0.3 }}>
                                            Embargos ({embargos.length})
                                        </Typography>
                                    </Box>
                                </Box>
                                <Box sx={{ px: 2, py: 1.5 }}>
                                    <Box display="flex" gap={1} flexWrap="wrap">
                                        {embargos.map((emb, idx) => {
                                            const label = [
                                                emb.estado || 'Sin estado',
                                                emb.monto != null ? `$${Number(emb.monto).toLocaleString('es-AR', { minimumFractionDigits: 2 })}` : null,
                                                emb.fecha ? new Date(emb.fecha + 'T00:00:00').toLocaleDateString('es-AR') : null,
                                            ].filter(Boolean).join(' - ');

                                            const detail = [
                                                emb.banco_embargante ? `Banco: ${emb.banco_embargante}` : null,
                                                emb.folio_deposito_judicial ? `Folio: ${emb.folio_deposito_judicial}` : null,
                                                emb.num_transaccion ? `Transacción: ${emb.num_transaccion}` : null,
                                                emb.cheque_judicial ? `Cheque: ${emb.cheque_judicial}` : null,
                                                emb.responsable ? `Responsable: ${emb.responsable}` : null,
                                            ].filter(Boolean).join(' | ');

                                            const colorMap = {
                                                'Diligenciado': { bg: '#fff3e0', color: '#e65100', border: '#ff9800' },
                                                'Trabado': { bg: '#fce4ec', color: '#c62828', border: '#ef5350' },
                                                'Libramiento de fondos': { bg: '#e8f5e9', color: '#2e7d32', border: '#66bb6a' },
                                                'Levantamiento': { bg: '#e3f2fd', color: '#1565c0', border: '#42a5f5' },
                                            };
                                            const colors = colorMap[emb.estado] || { bg: '#f5f5f5', color: '#616161', border: '#bdbdbd' };

                                            return (
                                                <Tooltip key={emb.id || idx} title={detail || 'Sin datos adicionales'} arrow>
                                                    <Chip
                                                        size="small"
                                                        label={label}
                                                        onClick={() => setEmbargoDetalle(emb)}
                                                        sx={{
                                                            backgroundColor: colors.bg,
                                                            color: colors.color,
                                                            fontWeight: 600,
                                                            fontSize: '0.7rem',
                                                            height: 26,
                                                            border: `1px solid ${colors.border}`,
                                                            cursor: 'pointer',
                                                            '&:hover': { opacity: 0.85 },
                                                        }}
                                                    />
                                                </Tooltip>
                                            );
                                        })}
                                    </Box>
                                </Box>
                            </>
                        )}

                        {/* RIESGO Y RELEVANCIA (solo judiciales) */}
                        {tipo === "judicial" && (
                            <>
                                <Box sx={{ px: 2, pt: 1, pb: 0.75, backgroundColor: '#fafbfc', borderTop: '1px solid #eee', borderBottom: '1px solid #eee' }}>
                                    <Box display="flex" alignItems="center" justifyContent="space-between">
                                        <Box display="flex" alignItems="center" gap={1}>
                                            <FlagIcon sx={{ fontSize: 16, color: themeColor }} />
                                            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#444', letterSpacing: 0.3 }}>
                                                Riesgo y Relevancia
                                            </Typography>
                                        </Box>
                                        {permRiesgo.update && (
                                        <Button size="small" startIcon={<EditIcon />} onClick={handleAbrirRiesgoRelevancia}
                                            sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.75rem', color: themeColor, minWidth: 'auto', py: 0.25 }}>
                                            Editar
                                        </Button>
                                        )}
                                    </Box>
                                </Box>
                                <Box sx={{ px: 1.5, py: 1.5 }}>
                                    {(() => {
                                        const allItems = [
                                            { label: "Rel. Económica", val: riesgoRelevancia?.relevancia_economica, tipo: 'economica', defaultVal: 4, getText: v => getTextoRelevancia(v, 'economica') },
                                            { label: "Rel. Social", val: riesgoRelevancia?.relevancia_social, tipo: 'otra', defaultVal: 4, getText: v => getTextoRelevancia(v, 'otra') },
                                            { label: "Rel. Jurídica", val: riesgoRelevancia?.relevancia_juridica, tipo: 'otra', defaultVal: 4, getText: v => getTextoRelevancia(v, 'otra') },
                                            { label: "Rel. Institucional", val: riesgoRelevancia?.relevancia_institucional, tipo: 'otra', defaultVal: 4, getText: v => getTextoRelevancia(v, 'otra') },
                                            { label: "Riesgo Judicial", val: riesgoRelevancia?.riesgo_judicial, tipo: 'economica', defaultVal: 4, getText: v => getTextoRiesgo(v) },
                                            { label: "Ej. Presupuestario", val: riesgoRelevancia?.ejercicio_presupuestario, tipo: 'ejercicio', defaultVal: 3, getText: v => getTextoEjercicio(v) },
                                            { label: "Acceso/Visibilidad", val: riesgoRelevancia?.acceso_visibilidad, tipo: 'acceso', defaultVal: 4, getText: v => getTextoAcceso(v) },
                                        ];
                                        const informados = allItems.filter(item => item.val != null && item.val !== item.defaultVal);
                                        if (informados.length === 0) {
                                            return (
                                                <Typography variant="body2" sx={{ color: '#aaa', fontStyle: 'italic', fontSize: '0.8rem' }}>
                                                    Sin datos de riesgo y relevancia informados
                                                </Typography>
                                            );
                                        }
                                        return (
                                            <Box display="flex" gap={1} flexWrap="wrap">
                                                {informados.map((item, idx) => {
                                                    const colors = item.tipo === 'ejercicio'
                                                        ? { bg: '#e3f2fd', color: '#1976d2', border: '#90caf9' }
                                                        : item.tipo === 'acceso'
                                                        ? { bg: item.val === 1 ? '#fff3e0' : '#f5f5f5', color: item.val === 1 ? '#e65100' : '#757575', border: item.val === 1 ? '#ff9800' : '#bdbdbd' }
                                                        : getColorChip(item.val, item.tipo);
                                                    return (
                                                        <Tooltip key={idx} title={item.label} arrow>
                                                            <Chip size="small" label={`${item.label}: ${item.getText(item.val)}`}
                                                                sx={{ backgroundColor: colors.bg, color: colors.color, fontWeight: 600, fontSize: '0.7rem', height: 26, border: `1px solid ${colors.border}` }}
                                                            />
                                                        </Tooltip>
                                                    );
                                                })}
                                            </Box>
                                        );
                                    })()}
                                </Box>
                            </>
                        )}
                    </Box>
                </Collapse>

                {/* Dialog para agregar área conjunta */}
                <Dialog open={dialogAreaConjunta} onClose={() => setDialogAreaConjunta(false)} maxWidth="sm" fullWidth>
                    <DialogTitle style={{ borderBottom: '1px solid #e0e0e0' }}>
                        Agregar Área Conjunta
                    </DialogTitle>
                    <DialogContent style={{ paddingTop: 20 }}>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                            Seleccione un área adicional para este expediente:
                        </Typography>
                        <Box mt={2}>
                            <TextField
                                select
                                fullWidth
                                label="Área"
                                value={moduloSeleccionado || ""}
                                onChange={(e) => setModuloSeleccionado(e.target.value)}
                                variant="outlined"
                                size="small"
                            >
                                <MenuItem value="">-- Seleccionar área --</MenuItem>
                                {modulosDisponibles
                                    .filter(m => {
                                        if (Number(m.id) === Number(data?.id_modulo)) return false;
                                        if (areasConjuntas.some(a => Number(a.id_modulo) === Number(m.id))) return false;
                                        return ![6, 7, 8, 9, 15, 16].includes(Number(m.id));
                                    })
                                    .map(m => (
                                        <MenuItem key={m.id} value={m.id}>{capitalizar(m.modulo || m.label || m.nombre || '')}</MenuItem>
                                    ))
                                }
                            </TextField>
                        </Box>
                    </DialogContent>
                    <DialogActions style={{ padding: '12px 24px', borderTop: '1px solid #e0e0e0' }}>
                        <Button onClick={() => setDialogAreaConjunta(false)} color="secondary">
                            Cancelar
                        </Button>
                        <Button
                            onClick={handleGuardarAreaConjunta}
                            color="primary"
                            variant="contained"
                            disabled={!moduloSeleccionado}
                            style={{ minWidth: 100 }}
                        >
                            Guardar
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* ⭐ AGREGAR: Dialog para Riesgo y Relevancia */}
                <Dialog
                    open={dialogRiesgoRelevancia}
                    onClose={() => setDialogRiesgoRelevancia(false)}
                    maxWidth="md"
                    fullWidth
                >
                    <DialogTitle style={{ borderBottom: '1px solid #e0e0e0', backgroundColor: '#f5f5f5' }}>
                        <Box display="flex" alignItems="center">
                            <span role="img" aria-label="alerta" style={{ marginRight: 8, fontSize: 20 }}>⚠️</span>
                            <Typography variant="h6" style={{ fontWeight: 600 }}>
                                Editar Riesgo y Relevancia
                            </Typography>
                        </Box>
                    </DialogTitle>
                    <DialogContent style={{ paddingTop: 24 }}>
                        <Grid container spacing={3}>
                            {/* Relevancia Económica */}
                            <Grid size={12}>
                                <Paper elevation={0} style={{ padding: 16, backgroundColor: '#fafafa', border: '1px solid #e0e0e0' }}>
                                    <Typography variant="subtitle2" style={{ fontWeight: 700, marginBottom: 12, color: '#333' }}>
                                        Relevancia Económica
                                    </Typography>
                                    <RadioGroup
                                        row
                                        value={riesgoRelevanciaForm.relevancia_economica}
                                        onChange={(e) => setRiesgoRelevanciaForm({ ...riesgoRelevanciaForm, relevancia_economica: parseInt(e.target.value) })}
                                        sx={{ mb: 1, gap: 1 }}
                                    >
                                        <FormControlLabel value={1} control={<Radio size="small" />} label="Alta" />
                                        {[1, 2].includes(Number(data?.id_modulo)) && (
                                            <>
                                                <FormControlLabel value={2} control={<Radio size="small" />} label="Media" />
                                                <FormControlLabel value={3} control={<Radio size="small" />} label="Baja" />
                                            </>
                                        )}
                                        <FormControlLabel value={4} control={<Radio size="small" />} label={[1, 2].includes(Number(data?.id_modulo)) ? "No Informada" : "No Registra"} />
                                    </RadioGroup>
                                    <TextField
                                        fullWidth
                                        multiline
                                        minRows={2}
                                        placeholder="Observaciones..."
                                        value={riesgoRelevanciaForm.obs_re}
                                        onChange={(e) => setRiesgoRelevanciaForm({ ...riesgoRelevanciaForm, obs_re: e.target.value })}
                                        inputProps={{ maxLength: 500 }}
                                        size="small"
                                    />
                                </Paper>
                            </Grid>

                            {/* Relevancia Social */}
                            <Grid size={{ xs: 12, md: 6 }}>
                                <Paper elevation={0} style={{ padding: 16, backgroundColor: '#fafafa', border: '1px solid #e0e0e0', height: '100%' }}>
                                    <Typography variant="subtitle2" style={{ fontWeight: 700, marginBottom: 12, color: '#333' }}>
                                        Relevancia Social
                                    </Typography>
                                    <RadioGroup
                                        row
                                        value={riesgoRelevanciaForm.relevancia_social}
                                        onChange={(e) => setRiesgoRelevanciaForm({ ...riesgoRelevanciaForm, relevancia_social: parseInt(e.target.value) })}
                                        sx={{ mb: 1, gap: 1 }}
                                    >
                                        <FormControlLabel value={1} control={<Radio size="small" />} label="Alta" />
                                        <FormControlLabel value={4} control={<Radio size="small" />} label="No Registra" />
                                    </RadioGroup>
                                    <TextField
                                        fullWidth
                                        multiline
                                        minRows={2}
                                        placeholder="Observaciones..."
                                        value={riesgoRelevanciaForm.obs_rs}
                                        onChange={(e) => setRiesgoRelevanciaForm({ ...riesgoRelevanciaForm, obs_rs: e.target.value })}
                                        inputProps={{ maxLength: 500 }}
                                        size="small"
                                    />
                                </Paper>
                            </Grid>

                            {/* Relevancia Jurídica */}
                            <Grid size={{ xs: 12, md: 6 }}>
                                <Paper elevation={0} style={{ padding: 16, backgroundColor: '#fafafa', border: '1px solid #e0e0e0', height: '100%' }}>
                                    <Typography variant="subtitle2" style={{ fontWeight: 700, marginBottom: 12, color: '#333' }}>
                                        Relevancia Jurídica
                                    </Typography>
                                    <RadioGroup
                                        row
                                        value={riesgoRelevanciaForm.relevancia_juridica}
                                        onChange={(e) => setRiesgoRelevanciaForm({ ...riesgoRelevanciaForm, relevancia_juridica: parseInt(e.target.value) })}
                                        sx={{ mb: 1, gap: 1 }}
                                    >
                                        <FormControlLabel value={1} control={<Radio size="small" />} label="Alta" />
                                        <FormControlLabel value={4} control={<Radio size="small" />} label="No Registra" />
                                    </RadioGroup>
                                    <TextField
                                        fullWidth
                                        multiline
                                        minRows={2}
                                        placeholder="Observaciones..."
                                        value={riesgoRelevanciaForm.obs_rj}
                                        onChange={(e) => setRiesgoRelevanciaForm({ ...riesgoRelevanciaForm, obs_rj: e.target.value })}
                                        inputProps={{ maxLength: 500 }}
                                        size="small"
                                    />
                                </Paper>
                            </Grid>

                            {/* Relevancia Institucional */}
                            <Grid size={{ xs: 12, md: 6 }}>
                                <Paper elevation={0} style={{ padding: 16, backgroundColor: '#fafafa', border: '1px solid #e0e0e0', height: '100%' }}>
                                    <Typography variant="subtitle2" style={{ fontWeight: 700, marginBottom: 12, color: '#333' }}>
                                        Relevancia Institucional
                                    </Typography>
                                    <RadioGroup
                                        row
                                        value={riesgoRelevanciaForm.relevancia_institucional}
                                        onChange={(e) => setRiesgoRelevanciaForm({ ...riesgoRelevanciaForm, relevancia_institucional: parseInt(e.target.value) })}
                                        sx={{ mb: 1, gap: 1 }}
                                    >
                                        <FormControlLabel value={1} control={<Radio size="small" />} label="Alta" />
                                        <FormControlLabel value={4} control={<Radio size="small" />} label="No Registra" />
                                    </RadioGroup>
                                    <TextField
                                        fullWidth
                                        multiline
                                        minRows={2}
                                        placeholder="Observaciones..."
                                        value={riesgoRelevanciaForm.obs_ri}
                                        onChange={(e) => setRiesgoRelevanciaForm({ ...riesgoRelevanciaForm, obs_ri: e.target.value })}
                                        inputProps={{ maxLength: 500 }}
                                        size="small"
                                    />
                                </Paper>
                            </Grid>

                            {/* Riesgo Judicial */}
                            <Grid size={{ xs: 12, md: 6 }}>
                                <Paper elevation={0} style={{ padding: 16, backgroundColor: '#fafafa', border: '1px solid #e0e0e0', height: '100%' }}>
                                    <Typography variant="subtitle2" style={{ fontWeight: 700, marginBottom: 12, color: '#333' }}>
                                        Riesgo Judicial
                                    </Typography>
                                    <RadioGroup
                                        row
                                        value={riesgoRelevanciaForm.riesgo_judicial}
                                        onChange={(e) => setRiesgoRelevanciaForm({ ...riesgoRelevanciaForm, riesgo_judicial: parseInt(e.target.value) })}
                                        sx={{ mb: 1, gap: 1 }}
                                    >
                                        <FormControlLabel value={1} control={<Radio size="small" />} label="Alta" />
                                        <FormControlLabel value={2} control={<Radio size="small" />} label="Media" />
                                        <FormControlLabel value={3} control={<Radio size="small" />} label="Baja" />
                                        <FormControlLabel value={4} control={<Radio size="small" />} label="No Informada" />
                                    </RadioGroup>
                                    <TextField
                                        fullWidth
                                        multiline
                                        minRows={2}
                                        placeholder="Observaciones..."
                                        value={riesgoRelevanciaForm.obs_rg}
                                        onChange={(e) => setRiesgoRelevanciaForm({ ...riesgoRelevanciaForm, obs_rg: e.target.value })}
                                        inputProps={{ maxLength: 500 }}
                                        size="small"
                                    />
                                </Paper>
                            </Grid>

                            {/* Ejercicio Presupuestario */}
                            <Grid size={{ xs: 12, md: 6 }}>
                                <Paper elevation={0} style={{ padding: 16, backgroundColor: '#fafafa', border: '1px solid #e0e0e0', height: '100%' }}>
                                    <Typography variant="subtitle2" style={{ fontWeight: 700, marginBottom: 12, color: '#333' }}>
                                        Ejercicio Presupuestario
                                    </Typography>
                                    <RadioGroup
                                        row
                                        value={riesgoRelevanciaForm.ejercicio_presupuestario}
                                        onChange={(e) => setRiesgoRelevanciaForm({ ...riesgoRelevanciaForm, ejercicio_presupuestario: parseInt(e.target.value) })}
                                        sx={{ mb: 1, gap: 1 }}
                                    >
                                        <FormControlLabel value={1} control={<Radio size="small" />} label="En Curso" />
                                        <FormControlLabel value={2} control={<Radio size="small" />} label="En el Próximo" />
                                        <FormControlLabel value={3} control={<Radio size="small" />} label="No Informada" />
                                    </RadioGroup>
                                    <TextField
                                        fullWidth
                                        multiline
                                        minRows={2}
                                        placeholder="Observaciones..."
                                        value={riesgoRelevanciaForm.obs_ep}
                                        onChange={(e) => setRiesgoRelevanciaForm({ ...riesgoRelevanciaForm, obs_ep: e.target.value })}
                                        inputProps={{ maxLength: 500 }}
                                        size="small"
                                    />
                                </Paper>
                            </Grid>

                            {/* Acceso y Visibilidad */}
                            <Grid size={{ xs: 12, md: 6 }}>
                                <Paper elevation={0} style={{ padding: 16, backgroundColor: '#fafafa', border: '1px solid #e0e0e0', height: '100%' }}>
                                    <Typography variant="subtitle2" style={{ fontWeight: 700, marginBottom: 12, color: '#333' }}>
                                        Acceso y Visibilidad
                                    </Typography>
                                    <RadioGroup
                                        row
                                        value={riesgoRelevanciaForm.acceso_visibilidad}
                                        onChange={(e) => setRiesgoRelevanciaForm({ ...riesgoRelevanciaForm, acceso_visibilidad: parseInt(e.target.value) })}
                                        sx={{ mb: 1, gap: 1 }}
                                    >
                                        <FormControlLabel value={1} control={<Radio size="small" />} label="Solo Fiscal" disabled={!esFiscal} />
                                        <FormControlLabel value={4} control={<Radio size="small" />} label="Normal" />
                                    </RadioGroup>
                                    {!esFiscal && (
                                        <Typography variant="caption" sx={{ color: '#999', fontStyle: 'italic' }}>
                                            Solo el Fiscal de Estado puede modificar esta opción
                                        </Typography>
                                    )}
                                    <TextField
                                        fullWidth
                                        multiline
                                        minRows={2}
                                        placeholder="Observaciones..."
                                        value={riesgoRelevanciaForm.obs_av}
                                        onChange={(e) => setRiesgoRelevanciaForm({ ...riesgoRelevanciaForm, obs_av: e.target.value })}
                                        inputProps={{ maxLength: 500 }}
                                        size="small"
                                    />
                                </Paper>
                            </Grid>
                        </Grid>
                    </DialogContent>
                    <DialogActions style={{ padding: '16px 24px', borderTop: '1px solid #e0e0e0', backgroundColor: '#f5f5f5' }}>
                        <Button
                            onClick={() => setDialogRiesgoRelevancia(false)}
                            color="secondary"
                            style={{ textTransform: 'none' }}
                        >
                            Cancelar
                        </Button>
                        <Button
                            onClick={handleGuardarRiesgoRelevancia}
                            color="primary"
                            variant="contained"
                            style={{
                                minWidth: 120,
                                textTransform: 'none',
                                fontWeight: 600
                            }}
                        >
                            Guardar
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Tabs con iconos */}
                <Tabs
                    value={tab}
                    onChange={(_, v) => { setTab(v); if (tabsArr[v] === "Historial") refreshHistorial(); loadEmbargos(); }}
                    indicatorColor="primary"
                    textColor="primary"
                    variant="scrollable"
                    scrollButtons="auto"
                    style={{
                        borderBottom: '2px solid #e0e0e0',
                        marginBottom: 16,
                        minHeight: 48
                    }}
                >
                    {tabsArr.map((t, i) => {
                        // ⭐ Mapeo de iconos
                        const getIcon = (label) => {
                            switch (label) {
                                case 'Radicación': return <HomeIcon fontSize="small" />;
                                case 'Certificados': return <DescriptionIcon fontSize="small" />;
                                case 'Resoluciones': return <GavelIcon fontSize="small" />;
                                case 'Partes': return <PeopleIcon fontSize="small" />;
                                case 'Movimientos': return <TimelineIcon fontSize="small" />;
                                case 'Vinculados': return <LinkIcon fontSize="small" />;
                                case 'Inmuebles': return <HomeIcon fontSize="small" />;
                                case 'Concursos y Quiebras': return <AccountBalanceIcon fontSize="small" />;
                                case 'Mediaciones': return <HandshakeIcon fontSize="small" />;
                                case 'Historial': return <HistoryIcon fontSize="small" />;
                                case 'Ubicación': return <LocationOnIcon fontSize="small" />;
                                case 'Pases': return <SwapHorizIcon fontSize="small" />;
                                case 'Solicitudes de Pago': return <PaymentIcon fontSize="small" />;
                                case 'Notas': return <StickyNote2Icon fontSize="small" />;
                                case 'Eventos': return <EventIcon fontSize="small" />;
                                default: return null;
                            }
                        };

                        return (
                            <Tooltip key={i} title={t} arrow>
                                <Tab
                                    icon={getIcon(t)}
                                    label={
                                        <Typography
                                            variant="caption"
                                            style={{
                                                fontSize: 11,
                                                display: 'block',
                                                marginTop: 4
                                            }}
                                        >
                                            {t}
                                        </Typography>
                                    }
                                    style={{
                                        fontWeight: tab === i ? 700 : 500,
                                        minWidth: 70,
                                        padding: '8px 8px'
                                    }}
                                />
                            </Tooltip>
                        );
                    })}
                </Tabs>

                {/* Contenido */}
                <Box mt={3}>
                    {/* Judicial */}
                    {tipo === "judicial" && tabsArr[tab] === "Radicación" && (
                        <RadicacionTab idExpediente={id} puedeEditar={permsExp.origen.editar} idModulo={idModuloActual} modulo={data?.modulo || data?.area} />
                    )}
                    {tipo === "judicial" && tabsArr[tab] === "Certificados" && (
                        <CertificadosTab
                            idExpediente={id}
                            idModulo={idModuloActual}
                            modulo={data?.modulo || data?.area}
                            usuario={usuario}
                            setSnackbar={setSnackbar}
                            permisos={{
                                puedeAgregar: permsExp.puedeEditarTab(1) || permsExp.puedeEditarTab(2),
                                puedeEditar: permsExp.puedeEditarTab(1) || permsExp.puedeEditarTab(2),
                                puedeEliminar: permsExp.puedeEditarTab(1) || permsExp.puedeEditarTab(2)
                            }}
                        />
                    )}
                    {tabsArr[tab] === "Resoluciones" && (
                        <ResolucionesTab idExpediente={id} />
                    )}
                    {tipo !== "judicial" && tabsArr[tab] === "Mediaciones" && (
                        <MediacionesTab idExpediente={id} setSnackbar={setSnackbar} modulo={data?.modulo || data?.area} puedeEditar={permsExp.puedeEditarTab(5) || permsExp.puedeEditarTab(15)} />
                    )}
                    {tipo === "judicial" && tabsArr[tab] === "Partes" && (
                        <PartesTab idExpediente={id} tipo={tipo} usuario={usuario} modulo={data?.modulo || data?.area} puedeEditar={permsExp.puedeEditarCompartido} />
                    )}
                    {tabsArr[tab] === "Movimientos" && (
                        <MovimientosTab
                            idExpediente={id}
                            tipoExpediente={tipo}
                            usuario={usuario?.usuario}
                            puedeEditar={permsExp.puedeEditarCompartido}
                            onEmbargoChange={loadEmbargos}
                            expedienteInfo={{
                                tipo,
                                nro_expediente: tipo === "judicial" ? data.autos_1_instancia : data.nro_expediente,
                                autos_1_instancia: data.autos_1_instancia,
                                caratula: data.caratula,
                                tipo_proceso: tipo === "judicial" ? data.tipo_proceso : data.extracto || data.tipo_proceso,
                                autos: tipo === "judicial" ? data.autos_1_instancia : undefined,
                                estado: tipo === "judicial" ? data.estado_proceso : data.estado,
                                estado_proceso: data.estado_proceso,
                                iniciador: tipo !== "judicial" ? data.iniciador : undefined,
                                extracto: tipo !== "judicial" ? data.extracto : undefined,
                                modulo: tipo !== "judicial" ? data.modulo : data.area, // ⭐ Unificar nombre
                                id_modulo: data.id_modulo, // ⭐ NUEVO: ID del módulo
                                fecha_inicio: data.fecha_inicio,
                                fecha_asignacion: data.fecha_asignacion
                            }}
                        />
                    )}
                    {tabsArr[tab] === "Vinculados" && (
                        <VinculadosTab
                            idExpediente={id}
                            modulo={data?.modulo || data?.area}
                            permisos={{
                                puedeAgregar: permsExp.puedeEditarCompartido,
                                puedeEliminar: permsExp.puedeEditarCompartido
                            }}
                            onChanged={refreshHistorial}
                        />
                    )}
                    {/* ⭐ NUEVO: Tab de Inmuebles */}
                    {tabsArr[tab] === "Inmuebles" && (
                        <InmueblesTab
                            idExpediente={id}
                            tipo={tipo}
                            modulo={data?.modulo || data?.area}
                            puedeEditar={permsExp.puedeEditarTab(3)}
                        />
                    )}
                    {/* ⭐ NUEVO: Tab de Concursos y Quiebras */}
                    {tabsArr[tab] === "Concursos y Quiebras" && (
                        <ConcursosTab
                            idExpediente={id}
                            tipo={tipo}
                            modulo={data?.modulo || data?.area}
                            puedeEditar={permsExp.puedeEditarTab(4)}
                        />
                    )}
                    {tabsArr[tab] === "Pases" && <PasesTab idExpediente={id} ubicacion={ubicacion} onUbicacionChange={cargarUbicacion} refreshKey={pasesRefreshKey} puedeEnviar={puedeEnviarPase} motivoNoEnviar={motivoNoPuedeEnviar} />}
                    {tabsArr[tab] === "Solicitudes de Pago" && <SolicitudesPagoTab idExpediente={id} />}
                    {tabsArr[tab] === "Eventos" && (
                        <EventosTab idExpediente={id} />
                    )}
                    {tabsArr[tab] === "Notas" && (
                        <NotasTab idExpediente={id} />
                    )}
                    {tabsArr[tab] === "Historial" && (
                        <HistorialCambios
                            historial={historial}
                            loading={loadingHistorial}
                            expedienteInfo={{
                                tipo,
                                nro_expediente: tipo === "judicial" ? data.autos_1_instancia : data.nro_expediente,
                                caratula: data.caratula,
                                tipo_proceso: tipo === "judicial" ? data.tipo_proceso : data.extracto || data.tipo_proceso,
                                autos: tipo === "judicial" ? data.autos_1_instancia : undefined,
                                estado: tipo === "judicial" ? data.estado_proceso : data.estado,
                                iniciador: tipo !== "judicial" ? data.iniciador : undefined,
                                extracto: tipo !== "judicial" ? data.extracto : undefined,
                                modulo: tipo !== "judicial" ? data.modulo : data.area, // ⭐ Unificar
                                id_modulo: data.id_modulo, // ⭐ NUEVO
                                fecha_inicio: data.fecha_inicio,
                                fecha_asignacion: data.fecha_asignacion
                            }}
                        />
                    )}
                </Box>
            </Paper>

            {loading && <div style={{ textAlign: "center", padding: 20 }}><CircularProgress /></div>}
            <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
                <AlertSnackbar severity={snackbar.severity}>{snackbar.message}</AlertSnackbar>
            </Snackbar>

            <ExpedienteForm
                open={openEdit}
                onClose={() => setOpenEdit(false)}
                onSave={handleSaveEdit}
                tipo={tipo}
                expediente={data}
                uiOverrides={uiAdminOverrides} // solo afecta Administrativo
            />

            {/* Carátula imprimible (solo exp administrativos) */}
            {tipo !== "judicial" && (
                <CaratulaAdminDialog open={caratulaOpen} onClose={() => setCaratulaOpen(false)} data={data} />
            )}

            {/* Dialog detalle de embargo */}
            <Dialog open={!!embargoDetalle} onClose={() => setEmbargoDetalle(null)} maxWidth="xs" fullWidth
                PaperProps={{ sx: { borderRadius: 1, overflow: 'hidden' } }}>
                {embargoDetalle && (() => {
                    const colorMap = {
                        'Diligenciado': { bg: '#fff3e0', color: '#e65100', border: '#ff9800', gradient: 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)' },
                        'Trabado': { bg: '#fce4ec', color: '#c62828', border: '#ef5350', gradient: 'linear-gradient(135deg, #ef5350 0%, #c62828 100%)' },
                        'Libramiento de fondos': { bg: '#e8f5e9', color: '#2e7d32', border: '#66bb6a', gradient: 'linear-gradient(135deg, #66bb6a 0%, #388e3c 100%)' },
                        'Levantamiento': { bg: '#e3f2fd', color: '#1565c0', border: '#42a5f5', gradient: 'linear-gradient(135deg, #42a5f5 0%, #1565c0 100%)' },
                    };
                    const c = colorMap[embargoDetalle.estado] || { bg: '#f5f5f5', color: '#616161', border: '#bdbdbd', gradient: 'linear-gradient(135deg, #9e9e9e 0%, #616161 100%)' };
                    const montoFmt = embargoDetalle.monto != null ? `$${Number(embargoDetalle.monto).toLocaleString('es-AR', { minimumFractionDigits: 2 })}` : null;
                    const fechaFmt = embargoDetalle.fecha ? new Date(embargoDetalle.fecha + 'T00:00:00').toLocaleDateString('es-AR') : null;
                    const fechaMovFmt = embargoDetalle.fecha_movimiento ? new Date(embargoDetalle.fecha_movimiento + 'T00:00:00').toLocaleDateString('es-AR') : null;

                    const DetailRow = ({ label, value }) => (
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.75 }}>
                            <Typography variant="body2" sx={{ color: '#888', fontSize: '0.8rem' }}>{label}</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 600, color: value ? '#333' : '#ccc', fontSize: '0.8rem', textAlign: 'right', ml: 2 }}>{value || '—'}</Typography>
                        </Box>
                    );

                    return (
                        <>
                            {/* Header con gradiente */}
                            <Box sx={{ background: c.gradient, color: '#fff', px: 2.5, pt: 2.5, pb: 2 }}>
                                <Box display="flex" alignItems="center" gap={1} mb={1}>
                                    <AccountBalanceIcon sx={{ fontSize: 20, opacity: 0.9 }} />
                                    <Typography variant="subtitle1" sx={{ fontWeight: 700, letterSpacing: 0.3 }}>
                                        Detalle del Embargo
                                    </Typography>
                                </Box>
                                <Chip
                                    size="small"
                                    label={embargoDetalle.estado || 'Sin estado'}
                                    sx={{ backgroundColor: 'rgba(255,255,255,0.25)', color: '#fff', fontWeight: 700, fontSize: '0.75rem', backdropFilter: 'blur(4px)' }}
                                />
                                {montoFmt && (
                                    <Typography variant="h5" sx={{ fontWeight: 800, mt: 1.5, letterSpacing: -0.5 }}>
                                        {montoFmt}
                                    </Typography>
                                )}
                                {fechaFmt && (
                                    <Typography variant="body2" sx={{ opacity: 0.85, mt: 0.25, fontSize: '0.8rem' }}>
                                        {fechaFmt}
                                    </Typography>
                                )}
                            </Box>

                            {/* Datos financieros */}
                            <Box sx={{ px: 2.5, pt: 2, pb: 1 }}>
                                <Typography variant="caption" sx={{ color: '#aaa', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, fontSize: '0.65rem' }}>
                                    Datos financieros
                                </Typography>
                                <Box sx={{ mt: 0.5, '& > *:not(:last-child)': { borderBottom: '1px solid #f5f5f5' } }}>
                                    <DetailRow label="Folio Depósito Judicial" value={embargoDetalle.folio_deposito_judicial} />
                                    <DetailRow label="Banco Embargante" value={embargoDetalle.banco_embargante} />
                                    <DetailRow label="Nº Transacción" value={embargoDetalle.num_transaccion} />
                                    <DetailRow label="Cheque Judicial" value={embargoDetalle.cheque_judicial} />
                                </Box>
                            </Box>

                            {/* Datos del movimiento */}
                            <Box sx={{ px: 2.5, pt: 1, pb: 2 }}>
                                <Typography variant="caption" sx={{ color: '#aaa', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, fontSize: '0.65rem' }}>
                                    Movimiento asociado
                                </Typography>
                                <Box sx={{ mt: 0.5, '& > *:not(:last-child)': { borderBottom: '1px solid #f5f5f5' } }}>
                                    <DetailRow label="Fecha Movimiento" value={fechaMovFmt} />
                                    <DetailRow label="Responsable" value={embargoDetalle.responsable} />
                                </Box>
                            </Box>

                            <DialogActions sx={{ px: 2.5, pb: 2 }}>
                                <Button onClick={() => setEmbargoDetalle(null)} variant="outlined" size="small"
                                    sx={{ borderRadius: 1, textTransform: 'none', borderColor: c.border, color: c.color, '&:hover': { borderColor: c.color, backgroundColor: c.bg } }}>
                                    Cerrar
                                </Button>
                            </DialogActions>
                        </>
                    );
                })()}
            </Dialog>
        </Box>
    );
}
